import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../context/AuthContext';
import { ImportModal } from '../components/ImportModal';

// ── Utility ───────────────────────────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="btn btn-secondary btn-sm" onClick={() => {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    }} style={{ fontSize:11 }}>{copied ? '✓ Copied' : '⎘ Copy'}</button>
  );
}

function confirm2(msg) { return window.confirm(msg); }

// ── Member Edit Modal (instructor or student) ─────────────────────────────────
function MemberEditModal({ member, orgId, onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: member.full_name, email: member.email, password: '', is_active: member.is_active });
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.put(`/api/organizations/${orgId}/members/${member.id}`, form);
      toast.success('Updated successfully'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };
  const deleteMember = async () => {
    if (!confirm2(`Delete ${member.full_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/organizations/${orgId}/members/${member.id}`);
      toast.success('Deleted'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 460 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 className="modal-title" style={{ margin:0 }}>✏️ Edit {member.role === 'instructor' ? 'Instructor' : 'Student'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>
        <form onSubmit={save}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" value={form.full_name} onChange={f('full_name')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={f('email')} required />
          </div>
          <div className="form-group">
            <label className="form-label">New Password <span style={{ fontSize:11, color:'var(--text-muted)' }}>(leave blank to keep current)</span></label>
            <div style={{ position:'relative' }}>
              <input className="form-input" type={showPwd ? 'text' : 'password'}
                value={form.password} onChange={f('password')} placeholder="Enter new password" />
              <button type="button" onClick={() => setShowPwd(p => !p)}
                style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)' }}>
                {showPwd ? '🙈' : '👁'}
              </button>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} id="ia" style={{ accentColor:'var(--accent-primary)' }} />
            <label htmlFor="ia" style={{ fontSize:13, cursor:'pointer' }}>Account Active</label>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'space-between', marginTop:20 }}>
            <button type="button" className="btn btn-danger btn-sm" onClick={deleteMember}>🗑 Delete</button>
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Assign Multiple Instructors to Exam Modal ─────────────────────────────────
function AssignInstructorModal({ exam, orgId, onClose, onSaved }) {
  const [instructors,    setInstructors]    = useState([]);
  const [selected,       setSelected]       = useState(new Set());
  const [loadedExisting, setLoadedExisting] = useState(false);
  const [saving,         setSaving]         = useState(false);

  // Fetch available instructors + currently assigned ones in parallel
  React.useEffect(() => {
    Promise.all([
      axios.get(`/api/organizations/${orgId}/instructors/status`).catch(() => ({ data: [] })),
      axios.get(`/api/exams/${exam.id}/instructors`).catch(() => ({ data: [] })),
    ]).then(([instrRes, assignedRes]) => {
      setInstructors(Array.isArray(instrRes.data) ? instrRes.data : []);
      const assignedIds = (Array.isArray(assignedRes.data) ? assignedRes.data : []).map(i => i.id);
      // Also include legacy single instructor_id
      if (exam.instructor_id) assignedIds.push(exam.instructor_id);
      setSelected(new Set(assignedIds));
      setLoadedExisting(true);
    });
  }, [exam.id, exam.instructor_id, orgId]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`/api/exams/${exam.id}/instructors`, { instructor_ids: [...selected] });
      toast.success(`${selected.size} instructor${selected.size !== 1 ? 's' : ''} assigned`);
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  if (!loadedExisting) return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:500, textAlign:'center', padding:40 }}>
        <div className="spinner" />
      </div>
    </div>
  );

  const available = instructors.filter(i => i.status === 'available');
  const busy      = instructors.filter(i => i.status === 'busy');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:520 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 className="modal-title" style={{ margin:0 }}>👨‍🏫 Assign Proctors — {exam.title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>

        <div className="alert alert-info" style={{ marginBottom:16, fontSize:12 }}>
          Select one or more instructors. All selected proctors can monitor this exam live, open/close it to students, and send messages.
        </div>

        {/* Currently selected summary */}
        <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>
          {selected.size === 0 ? 'No proctors selected' : `${selected.size} proctor${selected.size!==1?'s':''} selected`}
        </div>

        {/* Available instructors */}
        {available.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--accent-success)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>
              ✅ Available
            </div>
            {available.map(instr => (
              <div key={instr.id} onClick={() => toggle(instr.id)}
                style={{
                  display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                  marginBottom:6, borderRadius:8, cursor:'pointer', transition:'all 0.15s',
                  background: selected.has(instr.id) ? 'rgba(79,142,247,0.1)' : 'var(--bg-elevated)',
                  border: `1px solid ${selected.has(instr.id) ? 'var(--accent-primary)' : 'var(--border)'}`,
                }}>
                <div style={{
                  width:20, height:20, borderRadius:5, border:'2px solid',
                  borderColor: selected.has(instr.id) ? 'var(--accent-primary)' : 'var(--border)',
                  background: selected.has(instr.id) ? 'var(--accent-primary)' : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  flexShrink:0, transition:'all 0.15s',
                }}>
                  {selected.has(instr.id) && <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>✓</span>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{instr.full_name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{instr.email}</div>
                </div>
                <span className="badge badge-green" style={{ fontSize:10 }}>Available</span>
              </div>
            ))}
          </div>
        )}

        {/* Busy instructors */}
        {busy.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--accent-warning)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>
              🔴 Busy (assigned to other exams)
            </div>
            {busy.map(instr => (
              <div key={instr.id} onClick={() => toggle(instr.id)}
                style={{
                  display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                  marginBottom:6, borderRadius:8, cursor:'pointer', transition:'all 0.15s',
                  background: selected.has(instr.id) ? 'rgba(79,142,247,0.1)' : 'var(--bg-elevated)',
                  border: `1px solid ${selected.has(instr.id) ? 'var(--accent-primary)' : 'var(--border)'}`,
                }}>
                <div style={{
                  width:20, height:20, borderRadius:5, border:'2px solid',
                  borderColor: selected.has(instr.id) ? 'var(--accent-primary)' : 'var(--border)',
                  background: selected.has(instr.id) ? 'var(--accent-primary)' : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  flexShrink:0, transition:'all 0.15s',
                }}>
                  {selected.has(instr.id) && <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>✓</span>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{instr.full_name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{instr.email}</div>
                  <div style={{ fontSize:11, color:'var(--accent-warning)', marginTop:2 }}>
                    Busy: {(instr.assigned_exams||[]).map(e => e.title).join(', ')}
                  </div>
                </div>
                <span className="badge badge-red" style={{ fontSize:10 }}>Busy</span>
              </div>
            ))}
          </div>
        )}

        {instructors.length === 0 && (
          <div style={{ textAlign:'center', color:'var(--text-muted)', padding:'24px 0' }}>
            No instructors in your organization yet. Add instructors first.
          </div>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20, borderTop:'1px solid var(--border)', paddingTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? 'Saving...' : `Assign ${selected.size} Proctor${selected.size!==1?'s':''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Groups Manager Modal ──────────────────────────────────────────────────────
function GroupsModal({ orgId, students, onClose }) {
  const [groups, setGroups] = useState([]);
  const [selGroup, setSelGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/organizations/${orgId}/groups`)
      .then(r => setGroups(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [orgId]);

  const loadMembers = async (g) => {
    setSelGroup(g);
    const r = await axios.get(`/api/organizations/${orgId}/groups/${g.id}/members`);
    setMembers(r.data);
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`/api/organizations/${orgId}/groups`, { name: newName.trim() });
      setGroups(p => [...p, { ...r.data, member_count: 0 }]);
      setNewName('');
      toast.success('Group created');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setCreating(false); }
  };

  const deleteGroup = async (g) => {
    if (!confirm2(`Delete group "${g.name}"?`)) return;
    await axios.delete(`/api/organizations/${orgId}/groups/${g.id}`);
    setGroups(p => p.filter(x => x.id !== g.id));
    if (selGroup?.id === g.id) { setSelGroup(null); setMembers([]); }
    toast.success('Group deleted');
  };

  const addMember = async (sid) => {
    await axios.post(`/api/organizations/${orgId}/groups/${selGroup.id}/members`, { student_ids: [sid] });
    const r = await axios.get(`/api/organizations/${orgId}/groups/${selGroup.id}/members`);
    setMembers(r.data);
    setGroups(p => p.map(g => g.id===selGroup.id ? {...g, member_count: parseInt(g.member_count)+1} : g));
    toast.success('Student added to group');
  };

  const removeMember = async (sid) => {
    await axios.delete(`/api/organizations/${orgId}/groups/${selGroup.id}/members/${sid}`);
    setMembers(p => p.filter(m => m.student_id !== sid));
    toast.success('Removed from group');
  };

  const memberIds = members.map(m => m.student_id || m.id);
  const available = students.filter(s => !memberIds.includes(s.id));

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 680, maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 className="modal-title" style={{ margin:0 }}>👥 Student Groups</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>
        {loading ? <div style={{ textAlign:'center', padding:32 }}><div className="spinner" /></div> : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr', gap:16, overflow:'auto' }}>
            {/* Group list */}
            <div>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Groups ({groups.length})</div>
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <input className="form-input" style={{ padding:'6px 10px', fontSize:13 }}
                  value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="New group name" onKeyDown={e => e.key==='Enter' && createGroup()} />
                <button className="btn btn-primary btn-sm" onClick={createGroup} disabled={creating}>+</button>
              </div>
              {groups.map(g => (
                <div key={g.id} onClick={() => loadMembers(g)} style={{
                  padding:'10px 12px', marginBottom:6, borderRadius:8, cursor:'pointer',
                  background: selGroup?.id===g.id ? 'rgba(79,142,247,0.1)' : 'var(--bg-elevated)',
                  border: `1px solid ${selGroup?.id===g.id ? 'var(--accent-primary)' : 'var(--border)'}`,
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{g.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{g.member_count} students</div>
                  </div>
                  <button className="btn btn-danger btn-sm" style={{ fontSize:10, padding:'2px 6px' }}
                    onClick={e => { e.stopPropagation(); deleteGroup(g); }}>✕</button>
                </div>
              ))}
              {groups.length === 0 && <div style={{ fontSize:12, color:'var(--text-muted)' }}>No groups yet. Create one above.</div>}
            </div>
            {/* Members panel */}
            <div>
              {selGroup ? (
                <>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Members of "{selGroup.name}"</div>
                  {members.length === 0 && <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8 }}>No members yet.</div>}
                  {members.map(m => (
                    <div key={m.id||m.student_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 10px', background:'var(--bg-elevated)', borderRadius:6, marginBottom:4 }}>
                      <div><div style={{ fontSize:13, fontWeight:500 }}>{m.full_name}</div><div style={{ fontSize:11, color:'var(--text-muted)' }}>{m.email}</div></div>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:10 }} onClick={() => removeMember(m.student_id||m.id)}>Remove</button>
                    </div>
                  ))}
                  {available.length > 0 && (
                    <div style={{ marginTop:12 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:6 }}>Add students:</div>
                      <div style={{ maxHeight:160, overflowY:'auto' }}>
                        {available.map(s => (
                          <div key={s.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 8px', borderRadius:6, marginBottom:3 }}>
                            <div style={{ fontSize:12 }}>{s.full_name}</div>
                            <button className="btn btn-secondary btn-sm" style={{ fontSize:10 }} onClick={() => addMember(s.id)}>+ Add</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize:13, color:'var(--text-muted)', paddingTop:40, textAlign:'center' }}>
                  ← Select a group to manage its members
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Map Groups to Exam Modal ──────────────────────────────────────────────────
function MapGroupsModal({ exam, orgId, onClose, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [mapped, setMapped] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`/api/organizations/${orgId}/groups`),
      axios.get(`/api/organizations/${orgId}/exams/${exam.id}/groups`),
    ]).then(([gr, mr]) => {
      setGroups(gr.data);
      setMapped(mr.data);
      setSelected(new Set(mr.data.map(m => m.id)));
    }).finally(() => setLoading(false));
  }, [orgId, exam.id]);

  const toggle = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`/api/organizations/${orgId}/exams/${exam.id}/groups`, { group_ids: [...selected] });
      toast.success(`${res.data.groups_mapped} group(s) mapped, ${res.data.enrolled} student(s) enrolled`);
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 500 }}>
        <h3 className="modal-title">🗂 Map Groups to Exam — {exam.title}</h3>
        {loading ? <div style={{ textAlign:'center', padding:24 }}><div className="spinner" /></div> : (
          <>
            <div className="alert alert-info" style={{ marginBottom:14, fontSize:12 }}>
              Students in selected groups will be auto-enrolled and opted-in. They can opt out via the registration link.
            </div>
            {groups.length === 0 ? (
              <div style={{ textAlign:'center', color:'var(--text-muted)', padding:24 }}>No groups yet. Create groups first.</div>
            ) : groups.map(g => (
              <div key={g.id} onClick={() => toggle(g.id)} style={{
                display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                background: selected.has(g.id) ? 'rgba(79,142,247,0.08)' : 'var(--bg-elevated)',
                border: `1px solid ${selected.has(g.id) ? 'var(--accent-primary)' : 'var(--border)'}`,
                borderRadius:8, marginBottom:8, cursor:'pointer',
              }}>
                <input type="checkbox" checked={selected.has(g.id)} onChange={() => {}} style={{ accentColor:'var(--accent-primary)' }} />
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{g.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{g.member_count} students</div>
                </div>
                {mapped.find(m => m.id===g.id) && <span className="badge badge-green" style={{ marginLeft:'auto', fontSize:10 }}>Currently mapped</span>}
              </div>
            ))}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : `Map ${selected.size} Group(s)`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Opt Status Modal ──────────────────────────────────────────────────────────
function OptStatusModal({ exam, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/organizations/exams/${exam.id}/opt-summary`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [exam.id]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600, maxHeight:'80vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 className="modal-title" style={{ margin:0 }}>📊 Opt-In/Out — {exam.title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>
        {loading ? <div style={{ textAlign:'center', padding:24 }}><div className="spinner" /></div> : !data ? <div>Error loading</div> : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
              {[
                { label:'Total Enrolled', val:data.total_enrolled||0, color:'var(--accent-primary)' },
                { label:'Opted In',       val:data.opted_in||0,       color:'var(--accent-success)' },
                { label:'Opted Out',      val:data.opted_out||0,      color:'var(--accent-danger)' },
              ].map(s => (
                <div key={s.label} style={{ padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, textAlign:'center' }}>
                  <div style={{ fontSize:24, fontWeight:700, color:s.color }}>{s.val}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--border)' }}>
                <th style={{ textAlign:'left', padding:'6px 8px', color:'var(--text-muted)' }}>Student</th>
                <th style={{ textAlign:'left', padding:'6px 8px', color:'var(--text-muted)' }}>Status</th>
                <th style={{ textAlign:'left', padding:'6px 8px', color:'var(--text-muted)' }}>Reason</th>
              </tr></thead>
              <tbody>
                {(data.students||[]).map((s,i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'7px 8px' }}><div style={{ fontWeight:500 }}>{s.student_name}</div><div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.email}</div></td>
                    <td style={{ padding:'7px 8px' }}><span className={`badge badge-${s.opt_status==='in'?'green':'red'}`}>{s.opt_status==='in'?'Opted In':'Opted Out'}</span></td>
                    <td style={{ padding:'7px 8px', fontSize:12, color:'var(--text-muted)' }}>{s.opt_reason||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ── Exam Password Modal ───────────────────────────────────────────────────────
function ExamPasswordModal({ exam, onClose, onUpdated }) {
  const [pwd, setPwd]     = useState(exam.start_password || '');
  const [enabled, setEnabled] = useState(exam.start_password_enabled || false);
  const [saving, setSaving]   = useState(false);

  const regenerate = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`/api/exams/${exam.id}/regenerate-password`);
      setPwd(r.data.start_password);
      toast.success('New password generated');
      onUpdated();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    const newEnabled = !enabled;
    await axios.post(`/api/exams/${exam.id}/toggle-password`, { enabled: newEnabled });
    setEnabled(newEnabled);
    toast.success(newEnabled ? 'Password protection enabled' : 'Password protection disabled');
    onUpdated();
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 className="modal-title" style={{ margin:0 }}>🔑 Exam Start Password</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>
        <div className="form-group">
          <label className="form-label">Current Start Password</label>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <code style={{ flex:1, padding:'10px 14px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8, fontFamily:'var(--font-mono)', fontSize:20, fontWeight:700, letterSpacing:4, color:'var(--accent-primary)', textAlign:'center' }}>
              {pwd || '—'}
            </code>
            <CopyBtn text={pwd} />
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0' }}>
          <input type="checkbox" id="pwen" checked={enabled} onChange={toggleEnabled} style={{ accentColor:'var(--accent-primary)', width:16, height:16 }} />
          <label htmlFor="pwen" style={{ fontSize:13, cursor:'pointer' }}>Require this password to start the exam</label>
        </div>
        {enabled && <div className="alert alert-info" style={{ fontSize:12, marginBottom:14 }}>
          Students must enter this password before starting. Share it with students separately (e.g. written on board during exam).
        </div>}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-warning" disabled={saving} onClick={regenerate}>🔄 Regenerate Password</button>
        </div>
      </div>
    </div>
  );
}

// ── Groups Inline View (Groups tab) ──────────────────────────────────────────
function GroupsInlineView({ orgId, students, onManage }) {
  const [groups, setGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`/api/organizations/${orgId}/groups`)
      .then(r => setGroups(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  if (loading) return <div style={{ padding:32, textAlign:'center' }}><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontSize:14, fontWeight:600 }}>{groups.length} group{groups.length!==1?'s':''}</div>
        <button className="btn btn-primary btn-sm" onClick={onManage}>+ Manage Groups</button>
      </div>
      {groups.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>👥</div>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:8 }}>No Groups Yet</div>
          <div style={{ color:'var(--text-secondary)', marginBottom:20, fontSize:13 }}>
            Create student groups to batch-enroll students into exams.
          </div>
          <button className="btn btn-primary" onClick={onManage}>Create First Group</button>
        </div>
      ) : (
        <div className="card-grid" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))' }}>
          {groups.map(g => (
            <div key={g.id} className="card" style={{ padding:'16px 18px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:15 }}>{g.name}</div>
                <span style={{
                  background:'rgba(79,142,247,0.12)', color:'var(--accent-primary)',
                  borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:600,
                }}>
                  {g.member_count} students
                </span>
              </div>
              {g.description && <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:10 }}>{g.description}</div>}
              <button className="btn btn-secondary btn-sm" onClick={onManage} style={{ width:'100%', justifyContent:'center' }}>
                ✏️ Edit Group
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── OrgExamCard — clean Pre/During/Post accordion ────────────────────────────
function Section({ color, label, dot, children }) {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{ marginTop:10 }}>
      <button onClick={() => setOpen(o => !o)} style={{ background:'none', border:'none', cursor:'pointer', padding:0, width:'100%', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ width:7, height:7, borderRadius:4, background:color, display:'inline-block' }} />
        <span style={{ fontSize:10, fontWeight:800, color, textTransform:'uppercase', letterSpacing:'0.9px' }}>{label}</span>
        <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ paddingTop:8, display:'flex', flexWrap:'wrap', gap:6 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Btn({ label, color, onClick, disabled }) {
  return (
    <button className="btn btn-secondary btn-sm"
      style={{ color: color || 'var(--text-primary)', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={disabled ? undefined : onClick}>
      {label}
    </button>
  );
}

function OrgExamCard({ exam, navigate, onAssignInstr, onMapGroups, onOptStatus, onPwd, onToggleQA, onLoadLinks, onCreateLink, onDeactivateLink, links, baseUrl }) {
  return (
    <div className="card" style={{ marginBottom:14, borderLeft:`3px solid ${exam.proctor_published ? '#16a34a' : exam.is_published ? '#3b82f6' : '#6b7280'}` }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:16, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{exam.title}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3, display:'flex', gap:10, flexWrap:'wrap' }}>
            <span>❓ {exam.question_count||0} Qs</span>
            <span>⏱ {exam.duration_minutes}min</span>
            <span>🎯 {exam.total_marks} marks</span>
            <span>👥 {exam.enrolled_count||0} enrolled</span>
            {exam.instructor_name && <span>👨‍🏫 {exam.instructor_name}</span>}
            {exam.proctoring_enabled && <span style={{ color:'#f59e0b' }}>🔒 Proctored</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:5, flexWrap:'wrap', flexShrink:0 }}>
          {exam.is_ended
            ? <span className="badge badge-red">🛑 Ended</span>
            : <>
                <span className={`badge badge-${exam.is_published ? 'green' : 'yellow'}`}>{exam.is_published ? 'Published' : 'Draft'}</span>
                {exam.proctor_published && <span className="badge badge-green">🟢 Open</span>}
                {!exam.proctor_published && exam.is_published && <span className="badge badge-red">🔴 Closed</span>}
              </>
          }
        </div>
      </div>

      {/* ── PRE-EXAM ──────────────────────────────────────────────────── */}
      <Section color="#3b82f6" label="Pre-Exam Setup">
        <Btn label="✏️ Edit &amp; Publish" onClick={() => navigate('/manage/exams')} />
        <Btn label="👨‍🏫 Assign Proctor"  onClick={onAssignInstr} />
        <Btn label="🔗 Registration Links" onClick={() => { onLoadLinks(); }} />
        <Btn label="🔑 Exam Password"    onClick={onPwd} />
        <Btn label={exam.questions_hidden ? '🔒 Q&A Hidden' : '👁 Q&A Visible'}
          color={exam.questions_hidden ? 'var(--accent-danger)' : 'var(--accent-success)'}
          onClick={onToggleQA} />
      </Section>

      {/* ── DURING EXAM ───────────────────────────────────────────────── */}
      {exam.is_published && !exam.is_ended && (
        <Section color="#f59e0b" label="During Exam">
          <Btn label="📡 Live Monitor" color="var(--accent-primary)" onClick={() => navigate(`/live/${exam.id}`)} />
          <Btn label="📊 Opt-In Status" onClick={onOptStatus} />
        </Section>
      )}

      {/* ── POST-EXAM ─────────────────────────────────────────────────── */}
      <Section color="#16a34a" label="Reports &amp; Results">
        <Btn label="📋 Attendance Report" color="var(--accent-secondary)" onClick={() => navigate(`/reports/attendance/${exam.id}`)} />
        <Btn label="📥 Download Q&amp;A"  color="var(--accent-success)"   onClick={() => navigate(`/reports/qa/${exam.id}`)} />
      </Section>

      {/* ── Registration links inline ──────────────────────────────────── */}
      {links && (
        <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid var(--border)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <span style={{ fontSize:12, fontWeight:600 }}>🔗 Registration Links</span>
            <button className="btn btn-primary btn-sm" style={{ fontSize:11 }} onClick={onCreateLink}>+ New Link</button>
          </div>
          {links.length === 0
            ? <div style={{ fontSize:12, color:'var(--text-muted)' }}>No links yet — create one so students can register.</div>
            : links.map(link => (
              <div key={link.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:'1px solid var(--border)' }}>
                <span className={`badge badge-${link.is_active ? 'green' : 'red'}`} style={{ fontSize:10 }}>{link.is_active ? 'Active' : 'Off'}</span>
                <code style={{ flex:1, fontSize:11, fontFamily:'monospace', color:'var(--accent-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {baseUrl}/{link.token}
                </code>
                <span style={{ fontSize:11, color:'var(--text-muted)', flexShrink:0 }}>{link.uses_count} uses</span>
                <button className="btn btn-secondary btn-sm" style={{ fontSize:10 }}
                  onClick={() => { navigator.clipboard.writeText(`${baseUrl}/${link.token}`); }}>📋 Copy</button>
                {link.is_active && (
                  <button className="btn btn-danger btn-sm" style={{ fontSize:10 }} onClick={() => onDeactivateLink(link.id)}>Disable</button>
                )}
              </div>
            ))
          }
        </div>
      )}
      {!links && (
        <div style={{ marginTop:10 }}>
          <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }} onClick={onLoadLinks}>🔗 Load Registration Links</button>
        </div>
      )}
    </div>
  );
}

// ─── StudentsTab — exam-filtered student list with photos ─────────────────────
function StudentsTab({ orgId, exams, onImport }) {
  const [examId,      setExamId]      = React.useState('');
  const [students,    setStudents]    = React.useState([]);
  const [loading,     setLoading]     = React.useState(false);
  const [search,      setSearch]      = React.useState('');
  const [photoStudent, setPhotoStudent] = React.useState(null);
  const [selectedIds,  setSelectedIds]  = React.useState(new Set());

  const handleUnenroll = async (studentId, studentName) => {
    if (!window.confirm(`Remove ${studentName} from this exam?`)) return;
    await axios.delete(`/api/exams/${examId}/enrollments/${studentId}`);
    load(examId);
    setSelectedIds(new Set());
  };

  const handleBulkUnenroll = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedIds.size} students from this exam?`)) return;
    await Promise.all([...selectedIds].map(id =>
      axios.delete(`/api/exams/${examId}/enrollments/${id}`).catch(() => {})
    ));
    load(examId);
    setSelectedIds(new Set());
  };

  const load = (eid) => {
    setExamId(eid);
    if (!eid) { setStudents([]); return; }
    setLoading(true);
    axios.get(`/api/exams/${eid}/attendance/full`)
      .then(r => setStudents(r.data.students || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const filtered = students.filter(s =>
    !search || s.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select className="form-input" style={{ flex:1, minWidth:220 }}
          value={examId} onChange={e => { load(e.target.value); setSelectedIds(new Set()); }}>
          <option value="">— Select exam to view students —</option>
          {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
        </select>
        {examId && (
          <input className="form-input" style={{ width:200 }} placeholder="🔍 Search..."
            value={search} onChange={e => setSearch(e.target.value)} />
        )}
        {selectedIds.size > 0 && (
          <button className="btn btn-danger btn-sm" onClick={handleBulkUnenroll}>
            🗑 Remove {selectedIds.size} Selected
          </button>
        )}
        <button className="btn btn-primary btn-sm" onClick={onImport}>📥 Import Students</button>
      </div>

      {!examId && (
        <div className="card" style={{ textAlign:'center', padding:40, color:'var(--text-secondary)' }}>
          <div style={{ fontSize:32, marginBottom:10 }}>👥</div>
          <div style={{ fontWeight:600, marginBottom:6 }}>Select an exam above</div>
          <div style={{ fontSize:13 }}>Choose an exam to see all enrolled students</div>
        </div>
      )}

      {examId && loading && <div style={{ textAlign:'center', padding:40 }}><div className="spinner" /></div>}

      {examId && !loading && (
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontWeight:600, fontSize:14 }}>
            {filtered.length} student{filtered.length!==1?'s':''} enrolled
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding:32, textAlign:'center', color:'var(--text-secondary)', fontSize:13 }}>
              No students enrolled in this exam yet.
            </div>
          ) : (
            <div className="table-container" style={{ border:'none' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width:36 }}>
                      <input type="checkbox" title="Select all"
                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                        onChange={e => setSelectedIds(e.target.checked ? new Set(filtered.map(s => s.id)) : new Set())}
                        style={{ width:15, height:15, cursor:'pointer', accentColor:'var(--accent-primary)' }} />
                    </th>
                    <th>Photo</th><th>Name</th><th>Email</th><th>Status</th><th>Score</th><th>Violations</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s, i) => (
                    <tr key={s.id}>
                      <td>
                        <input type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={e => {
                            const next = new Set(selectedIds);
                            e.target.checked ? next.add(s.id) : next.delete(s.id);
                            setSelectedIds(next);
                          }}
                          style={{ width:15, height:15, cursor:'pointer', accentColor:'var(--accent-primary)' }} />
                      </td>
                      <td>
                        <div style={{ position:'relative', width:40, height:40 }}>
                          {s.photo_url ? (
                            <img src={s.photo_url} alt={s.full_name}
                              style={{ width:40, height:40, borderRadius:20, objectFit:'cover', border:'2px solid var(--border)' }} />
                          ) : (
                            <div style={{ width:40, height:40, borderRadius:20, background:'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:16 }}>
                              {s.full_name?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ fontWeight:600 }}>{s.full_name}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{s.email}</td>
                      <td>
                        {s.attempt_status
                          ? <span className={`badge badge-${s.attempt_status==='submitted'?'green':s.attempt_status==='terminated'?'red':'yellow'}`}>{s.attempt_status}</span>
                          : <span style={{ fontSize:12, color:'var(--text-muted)' }}>Not started</span>}
                      </td>
                      <td style={{ fontWeight:600 }}>{s.score != null ? `${s.score}/${s.total_marks||'—'}` : '—'}</td>
                      <td style={{ textAlign:'center' }}>
                        {s.violations_count > 0
                          ? <span style={{ color:'var(--accent-danger)', fontWeight:700 }}>{s.violations_count}</span>
                          : <span style={{ color:'var(--text-muted)' }}>0</span>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }}
                            onClick={() => setPhotoStudent(s)}>
                            {s.photo_url ? '🖼 Change Photo' : '📷 Upload Photo'}
                          </button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize:11 }}
                            onClick={() => handleUnenroll(s.id, s.full_name)}>
                            ✕ Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Photo upload modal */}
      {photoStudent && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:360, textAlign:'center' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 className="modal-title" style={{ margin:0 }}>📷 Student Photo</h3>
              <button onClick={() => setPhotoStudent(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
            </div>
            <div style={{ marginBottom:12, fontSize:14, color:'var(--text-secondary)' }}>{photoStudent.full_name}</div>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
              <ImageUploader
                currentUrl={photoStudent.photo_url}
                shape="circle"
                size={120}
                label="Upload Photo"
                hint="JPG or PNG, clear face photo, max 2MB"
                onUpload={async (base64) => {
                  await axios.post(`/api/organizations/${orgId}/upload`, {
                    file_data: base64, type: 'photo', target_id: photoStudent.id
                  });
                  // Refresh the student list
                  load(examId);
                  setPhotoStudent(null);
                  const { toast } = await import('react-toastify');
                  toast.success('Photo uploaded!');
                }}
              />
            </div>
            <button className="btn btn-secondary w-full" style={{ justifyContent:'center' }} onClick={() => setPhotoStudent(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main OrgDashboard ────────────────────────────────────────────────────────
export default function OrgDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = user?.org_id;
  const isOrg = user?.role === 'organization';
  const isInstructor = user?.role === 'instructor';

  const [stats,          setStats]          = useState({});
  const [exams,          setExams]          = useState([]);
  const [members,        setMembers]        = useState([]);
  const [instrStatus,    setInstrStatus]    = useState([]);
  const [links,          setLinks]          = useState({});
  const [activeTab,      setActiveTab]      = useState('overview');
  const [loading,        setLoading]        = useState(true);
  const [importModal,    setImportModal]    = useState(null);
  const [editMember,     setEditMember]     = useState(null);
  const [assignInstr,    setAssignInstr]    = useState(null);
  const [mapGroupsExam,  setMapGroupsExam]  = useState(null);
  const [optStatusExam,  setOptStatusExam]  = useState(null);
  const [pwdExam,        setPwdExam]        = useState(null);
  const [showGroups,     setShowGroups]     = useState(false);
  const [showAddInstr,   setShowAddInstr]   = useState(false);
  const [instrForm,      setInstrForm]      = useState({ email:'', full_name:'', password:'' });
  const [orgInfo,        setOrgInfo]        = useState(null);

  const [importStudentExam, setImportStudentExam] = useState('');

  const handleImportStudents = async (rows) => {
    const r = await axios.post(`/api/organizations/${orgId}/import/students`, {
      rows,
      exam_id: importStudentExam || undefined,  // directly enroll in exam if selected
    });
    fetchAll();
    const examTitle = exams.find(e => e.id === importStudentExam)?.title;
    return { ...r.data, examTitle };
  };
  const handleImportInstructors = async (rows) => { const r = await axios.post(`/api/organizations/${orgId}/import/instructors`, { rows }); fetchAll(); return r.data; };

  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    try {
      const [statsR, examsR, membersR, orgR, instrR] = await Promise.all([
        axios.get(`/api/organizations/${orgId}/stats`),
        axios.get('/api/exams/manage'),
        axios.get(`/api/organizations/${orgId}/members`),
        axios.get(`/api/organizations/${orgId}`),
        axios.get(`/api/organizations/${orgId}/instructors/status`),
      ]);
      setStats({ ...statsR.data, logo_url: orgR.data?.logo_url });
      setExams(examsR.data);
      setMembers(membersR.data);
      setOrgInfo(orgR.data);
      setInstrStatus(instrR.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchLinks = async (examId) => {
    try { const r = await axios.get(`/api/organizations/links/exam/${examId}`); setLinks(p => ({ ...p, [examId]: r.data })); } catch {}
  };
  const createLink = async (examId) => {
    const desc = window.prompt('Link description:', 'Main registration link');
    if (desc === null) return;
    try { await axios.post(`/api/organizations/links/exam/${examId}`, { description: desc }); toast.success('Link created'); fetchLinks(examId); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };
  const deactivateLink = async (linkId, examId) => {
    if (!confirm2('Deactivate this registration link? Students can no longer use it.')) return;
    await axios.delete(`/api/organizations/links/${linkId}`);
    toast.success('Link deactivated'); fetchLinks(examId);
  };
  const addInstructor = async (e) => {
    e.preventDefault();
    try { await axios.post(`/api/organizations/${orgId}/instructors`, instrForm); toast.success('Instructor added'); setShowAddInstr(false); setInstrForm({ email:'', full_name:'', password:'' }); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const baseUrl = `${window.location.origin}/register`;
  const instructors = members.filter(m => m.role === 'instructor');
  const students    = members.filter(m => m.role === 'student');

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const TABS = ['overview','exams','instructors','students'];

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        {/* Header with logo */}
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            {/* Logo upload — click to change */}
            <ImageUploader
              currentUrl={orgInfo?.logo_url}
              shape="rect"
              size={52}
              label="Logo"
              hint=""
              onUpload={async (base64) => {
                await axios.post(`/api/organizations/${orgId}/upload`, { file_data: base64, type: 'logo' });
                fetchAll();
                toast.success('Logo updated!');
              }}
            />
            <div>
              <h1 className="page-title" style={{ margin:0 }}>Organization Portal</h1>
              <p className="page-subtitle" style={{ margin:0 }}>{user?.org_name}</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/manage/exams')}>+ Create Exam</button>
        </div>

        {/* Stats */}
        <div className="card-grid card-grid-4 mb-6">
          {[
            { val:stats.students||0,       label:'Students',    color:'var(--accent-primary)' },
            { val:stats.instructors||0,    label:'Instructors', color:'var(--accent-secondary)' },
            { val:stats.exams_total||stats.exams||0, label:'Exams', color:'var(--accent-warning)' },
            { val:stats.attempts||0,       label:'Attempts',    color:'var(--accent-success)' },
          ].map(s => <div key={s.label} className="stat-card"><div className="stat-value" style={{ color:s.color }}>{s.val}</div><div className="stat-label">{s.label}</div></div>)}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          {TABS.map(tab => (
            <button key={tab} className="btn btn-secondary btn-sm" onClick={() => setActiveTab(tab)}
              style={activeTab===tab ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {tab === 'groups' ? '👥 Groups' : tab.charAt(0).toUpperCase()+tab.slice(1)}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ──────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div className="card">
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:14 }}>Quick Actions</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <button className="btn btn-primary" onClick={() => navigate('/manage/exams')}>+ Create Exam</button>
                <button className="btn btn-secondary" onClick={() => setActiveTab('exams')}>🔗 Registration Links</button>
                <button className="btn btn-secondary" onClick={() => setActiveTab('instructors')}>👨‍🏫 Manage Instructors</button>
              </div>
            </div>
            <div className="card">
              <h3 style={{ fontSize:15, fontWeight:600, marginBottom:14 }}>Workflow</h3>
              <ol style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:2.2, paddingLeft:20, margin:0 }}>
                <li>Create exam → set start password if needed</li>
                <li>Create registration link → share with students</li>
                <li>Create student groups → map groups to exam</li>
                <li>Assign instructor → they open exam to students</li>
                <li>Monitor via 📡 Live Monitor</li>
              </ol>
            </div>
          </div>
        )}

        {/* ── EXAMS ─────────────────────────────────────────────────── */}
        {activeTab === 'exams' && (
          <div>
            {exams.length === 0 ? (
              <div className="card" style={{ textAlign:'center', padding:40 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                <div style={{ fontWeight:600, fontSize:16, marginBottom:8 }}>No exams yet</div>
                <p style={{ color:'var(--text-secondary)', marginBottom:20 }}>Create your first exam from Manage Exams</p>
                <button className="btn btn-primary" onClick={() => navigate('/manage/exams')}>Go to Manage Exams</button>
              </div>
            ) : (
              exams.map(exam => (
                <OrgExamCard key={exam.id} exam={exam} navigate={navigate}
                  onAssignInstr={() => setAssignInstr({ ...exam })}
                  onOptStatus={() => setOptStatusExam(exam)}
                  onPwd={() => setPwdExam(exam)}
                  onToggleQA={() => {
                    const hidden = !exam.questions_hidden;
                    axios.post(`/api/exams/${exam.id}/toggle-questions-hidden`, { hidden })
                      .then(() => { toast.success(hidden ? '🔒 Q&A hidden from proctors' : '👁 Q&A visible'); fetchAll(); })
                      .catch(() => toast.error('Failed'));
                  }}
                  onLoadLinks={() => fetchLinks(exam.id)}
                  onCreateLink={() => createLink(exam.id)}
                  onDeactivateLink={(lid) => deactivateLink(lid, exam.id)}
                  links={links[exam.id]}
                  baseUrl={baseUrl}
                />
              ))
            )}
          </div>
        )}

        {/* ── INSTRUCTORS ────────────────────────────────────────────── */}
        {activeTab === 'instructors' && (
          <div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginBottom:14 }}>
              <button className="btn btn-secondary" onClick={() => setImportModal('instructors')}>📥 Import</button>
              <button className="btn btn-primary" onClick={() => setShowAddInstr(true)}>+ Add Instructor</button>
            </div>
            {showAddInstr && (
              <div className="modal-overlay">
                <div className="modal" style={{ maxWidth:440 }}>
                  <h3 className="modal-title">Add Instructor / Proctor</h3>
                  <form onSubmit={addInstructor}>
                    {[['full_name','Full Name','text','Dr. John Smith'],['email','Email','email','instr@org.com'],['password','Password','password','Min 6 chars']].map(([k,l,t,p]) => (
                      <div key={k} className="form-group"><label className="form-label">{l}</label>
                        <input className="form-input" type={t} placeholder={p} value={instrForm[k]} onChange={e => setInstrForm(p => ({...p, [k]:e.target.value}))} required /></div>
                    ))}
                    <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowAddInstr(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary">Add Instructor</button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            <div className="card" style={{ padding:0 }}>
              <div className="table-container" style={{ border:'none' }}>
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Assigned Exams</th><th>Actions</th></tr></thead>
                  <tbody>
                    {instrStatus.length === 0 && <tr><td colSpan={5} style={{ textAlign:'center', color:'var(--text-secondary)', padding:32 }}>No instructors yet.</td></tr>}
                    {instrStatus.map(m => (
                      <tr key={m.id}>
                        <td style={{ fontWeight:500 }}>{m.full_name}</td>
                        <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{m.email}</td>
                        <td><span className={`badge badge-${m.status==='available'?'green':'red'}`}>{m.status==='available'?'✅ Available':'🔴 Busy'}</span></td>
                        <td style={{ fontSize:12 }}>
                          {(m.assigned_exams||[]).length === 0 ? <span style={{ color:'var(--text-muted)' }}>None</span>
                            : (m.assigned_exams||[]).map(e => <div key={e.id} style={{ color:'var(--text-secondary)' }}>{e.title}</div>)}
                        </td>
                        <td>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditMember(members.find(x=>x.id===m.id)||m)}>✏️ Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── STUDENTS ───────────────────────────────────────────────── */}
        {activeTab === 'students' && (
          <StudentsTab orgId={orgId} exams={exams} onImport={() => setImportModal('students')} />
        )}

      </main>

      {/* ── Modals */}
      {assignInstr && <AssignInstructorModal exam={assignInstr} orgId={orgId} onClose={() => setAssignInstr(null)} onSaved={fetchAll} />}
      {optStatusExam && <OptStatusModal exam={optStatusExam} onClose={() => setOptStatusExam(null)} />}
      {pwdExam && <ExamPasswordModal exam={pwdExam} onClose={() => setPwdExam(null)} onSaved={fetchAll} />}
      {editMember && <MemberEditModal member={editMember} orgId={orgId} onClose={() => setEditMember(null)} onSaved={fetchAll} />}
      {importModal === 'students' && (
        <>
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:480 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 className="modal-title" style={{ margin:0 }}>📥 Import Students</h3>
                <button onClick={() => { setImportModal(null); setImportStudentExam(''); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
              </div>
              <div className="form-group" style={{ marginBottom:16 }}>
                <label className="form-label">📋 Enroll directly into exam <span style={{ fontSize:11, color:'var(--text-muted)' }}>(recommended)</span></label>
                <select className="form-input" value={importStudentExam} onChange={e => setImportStudentExam(e.target.value)}>
                  <option value="">— No auto-enrollment —</option>
                  {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
                </select>
                {importStudentExam ? (
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:5 }}>Students will be created and immediately enrolled in the selected exam.</div>
                ) : (
                  <div style={{ fontSize:11, color:'var(--accent-warning)', marginTop:6, padding:'8px 10px', background:'rgba(245,158,11,0.08)', borderRadius:6, border:'1px solid rgba(245,158,11,0.2)' }}>
                    ⚠ No exam selected — students will be created but <strong>NOT enrolled</strong> anywhere. Select an exam above if you want them enrolled now.
                  </div>
                )}
              </div>
              <button className="btn btn-primary w-full" style={{ justifyContent:'center', marginBottom:8 }}
                onClick={() => setImportModal('students_file')}>Continue to Upload →</button>
              <button className="btn btn-secondary w-full" style={{ justifyContent:'center' }}
                onClick={() => { setImportModal(null); setImportStudentExam(''); }}>Cancel</button>
            </div>
          </div>
        </>
      )}
      {importModal === 'students_file' && <ImportModal title="Import Students" templateFilename="students_template.csv"
        columns={[{ key:'full_name',label:'Full Name',required:true,example:'Aarav Singh',aliases:['name','Name'] },{ key:'email',label:'Email',required:true,example:'aarav@org.com' },{ key:'password',label:'Password',required:false,example:'Student@123' }]}
        onImport={handleImportStudents} onClose={() => { setImportModal(null); setImportStudentExam(''); }} />}
      {importModal === 'instructors' && <ImportModal title="Import Instructors" templateFilename="instructors_template.csv"
        columns={[{ key:'full_name',label:'Full Name',required:true,example:'Dr. Rajesh',aliases:['name','Name'] },{ key:'email',label:'Email',required:true,example:'rajesh@org.com' },{ key:'password',label:'Password',required:false,example:'Instructor@123' }]}
        onImport={handleImportInstructors} onClose={() => setImportModal(null)} />}
    </div>
  );
}
